import { Overlay } from '@angular/cdk/overlay';
import {
  Directive,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Renderer2,
  SimpleChange,
  SimpleChanges,
  ViewContainerRef,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { ISession, Visibility } from '../models/session';
import { DeviceAndBrowserDetectorService } from '../services/device-and-browser-detector.service';
import { UserService } from '../services/user.service';
import { Placement, TooltipDirective } from '../standalones/directives/pncl-tooltip.directive';
import { blockEvent } from '../utilities/dom-events.utils';

/**
 * Directive that restricts interaction with visibility controls based on configurable business rules.
 *
 * This directive is applied to UI elements (such as visibility dropdowns) and
 * automatically disables them based on institutional policies or other business logic.
 * When restricted, the directive:
 * - Visually indicates the disabled state by reducing opacity to 50%
 * - Prevents all click interactions by blocking and stopping event propagation
 * - Displays an informative tooltip explaining why the control is disabled
 *
 * Current restriction logic applies when:
 * 1. The space's institution has `preventMakingSpacesPublic` enabled in their settings
 * 2. The current visibility is NOT already set to PUBLIC (restricts changing spaces to public)
 *
 * This allows institutions to prevent users from changing spaces to public,
 * while still allowing management of already public spaces.
 *
 * @Input appRestrictVisibilityControl - Session object to extract data from
 * @Input appRestrictVisibilityControlIsEnabled - Enable/disable the directive (default: true)
 * @Input appRestrictVisibilityControlTooltipPlacement - Tooltip placement position (default: 'top-center')
 * @Input appRestrictVisibilityControlTooltipArrow - Tooltip arrow direction (default: 'bottom')
 */
@Directive({
  selector: '[appRestrictVisibilityControl]',
})
export class RestrictVisibilityControlDirective implements OnInit, OnChanges, OnDestroy {
  @Input('appRestrictVisibilityControl') space!: ISession;
  @Input() appRestrictVisibilityControlIsEnabled: boolean = true;
  @Input() appRestrictVisibilityControlTooltipPlacement: Placement = 'top-center';
  @Input() appRestrictVisibilityControlTooltipArrow: TooltipDirective['arrow'] = 'bottom';

  private tooltipDirective: TooltipDirective;
  private element!: HTMLElement;

  constructor(
    private renderer: Renderer2,
    private el: ElementRef,
    private userService: UserService,
    private translate: TranslateService,
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
    private deviceService: DeviceAndBrowserDetectorService,
    private zone: NgZone,
  ) {
    this.tooltipDirective = new TooltipDirective(
      this.overlay,
      this.el,
      this.viewContainerRef,
      this.deviceService,
      this.zone,
    );
  }

  ngOnInit(): void {
    this.element = this.el.nativeElement;

    this.tooltipDirective.appTooltip = this.translate.instant(
      'You cannot modify this permission because it is set by your institution',
    );
    this.tooltipDirective.appTooltipTextStyle = 'max-width: 173px; line-height: 19px;';
    this.tooltipDirective.appTooltipWrapText = true;
    this.tooltipDirective.placement = this.appRestrictVisibilityControlTooltipPlacement;
    this.tooltipDirective.arrow = this.appRestrictVisibilityControlTooltipArrow;
    this.tooltipDirective.disabled = true;
    this.tooltipDirective.ngOnInit();

    // Handle initial setup
    this.updateRestriction();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const relevantChanges = ['space', 'appRestrictVisibilityControlIsEnabled'];
    const hasRelevantChange = relevantChanges.some(
      (key) => changes[key] && !changes[key].firstChange,
    );

    if (hasRelevantChange) {
      this.updateRestriction();
    }
  }

  ngOnDestroy(): void {
    this.element.removeEventListener('click', blockEvent, true);
    this.tooltipDirective.ngOnDestroy();
  }

  private async updateRestriction(): Promise<void> {
    // If directive is disabled, remove all restrictions and return
    if (!this.appRestrictVisibilityControlIsEnabled) {
      this.applyRestrictionState(false);
      return;
    }

    /* On opening from space details (e.g., spaces manager)
       Institution object isn't populated for current space
       like when opened from inside the spaces using active space.
       A call is made to get institution to check preventMakingSpacesPublic setting.
       This follows the same pattern used for analytics in space details.
    **/
    if (!this.space?.institution && this.space?.institutionID) {
      // Cache it on the space object for future use
      this.space.institution = await firstValueFrom(
        this.userService.getInstitutionDetails(this.space.institutionID),
      );
    }

    const preventMakingSpacesPublic =
      this.space?.institution?.settings?.preventMakingSpacesPublic || false;
    const isRestricted = preventMakingSpacesPublic && this.space?.visibility !== Visibility.PUBLIC;

    this.applyRestrictionState(isRestricted);
  }

  /**
   * Applies or removes restriction state to the element.
   * @param isRestricted - Whether the element should be restricted
   */
  private applyRestrictionState(isRestricted: boolean): void {
    if (isRestricted) {
      // Add opacity class to visually indicate the element is disabled.
      this.renderer.addClass(this.element, 'opacity-50');
      this.element.addEventListener('click', blockEvent, true);
    } else {
      // Remove opacity class to restore the element to its original state.
      this.renderer.removeClass(this.element, 'opacity-50');
      this.element.removeEventListener('click', blockEvent, true);
    }

    // Update the tooltip disabled state.
    this.tooltipDirective.disabled = !isRestricted;
    this.tooltipDirective.ngOnChanges({
      disabled: new SimpleChange(null, !isRestricted, false),
    });
  }
}
